/**
 * WhatsApp links (F3-I) — `wa.me` with an interpolated template.
 *
 * v1 WITHOUT the Business API: a `https://wa.me/<E164>?text=<message>` link opens the chat
 * with the message pre-loaded. Used in: leads kanban cards (quick action),
 * lead timeline (logs a `whatsapp` event on click), and overdue-invoice
 * rows ("Collect via WhatsApp" with name/amount variables).
 *
 * Phone normalization is Venezuela-first and MUST match
 * `packages/core-rust/src/crm/phone.rs` (same behavior on client and server).
 */

/** Interpolation variables (e.g. `{ nombre: "Ada", curso: "Python", monto: "$50" }`). */
export type WaVars = Record<string, string | number | null | undefined>;

/**
 * Normalizes a phone number to E.164 (`+` + 8–15 digits). Default country: Venezuela.
 * Returns `null` if a plausible E.164 can't be formed.
 */
export function normalizePhoneVE(input: string): string | null {
  const trimmed = (input ?? "").trim();
  if (!trimmed) {
    return null;
  }
  const hadPlus = trimmed.startsWith("+");
  const digits = trimmed.replaceAll(/\D/g, "");
  if (!digits) {
    return null;
  }

  let e164: string;
  if (hadPlus) {
    e164 = `+${digits}`;
  } else if (digits.startsWith("58") && digits.length - 2 >= 9) {
    e164 = `+58${digits.slice(2)}`;
  } else if (digits.startsWith("0") && digits.length === 11) {
    e164 = `+58${digits.slice(1)}`;
  } else if (digits.length === 10 && (digits.startsWith("4") || digits.startsWith("2"))) {
    e164 = `+58${digits}`;
  } else {
    e164 = `+${digits}`;
  }

  const count = e164.replaceAll(/\D/g, "").length;
  return count >= 8 && count <= 15 ? e164 : null;
}

/**
 * Interpolates `{{key}}` in a template using `vars`. Missing keys are
 * substituted with an empty string (never leaves `{{...}}` visible). Doesn't introduce
 * HTML (the text goes URL-encoded when opening wa.me).
 */
export function interpolateWaTemplate(template: string, vars: WaVars = {}): string {
  return (template ?? "").replaceAll(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_m, key: string) => {
    const v = vars[key];
    return v === null || v === undefined ? "" : String(v);
  });
}

/**
 * Builds a `wa.me` link. Returns `null` if the phone number is not valid.
 * `templateBody` may contain variables `{{nombre}}`, `{{curso}}`, `{{monto}}`…
 */
export function buildWaLink(phone: string, templateBody: string, vars: WaVars = {}): string | null {
  const e164 = normalizePhoneVE(phone);
  if (!e164) {
    return null;
  }
  const number = e164.replace(/^\+/, "");
  const text = interpolateWaTemplate(templateBody, vars);
  const query = text ? `?text=${encodeURIComponent(text)}` : "";
  return `https://wa.me/${number}${query}`;
}
